import { HarmonySpec, Measure, NoteEvent, PedalSpec, PieceCore } from './piece';
import { preludeFiguration } from './figuration';
import { midiOf, parsePitch, parsePitchClass, pitchName, samePc, transposePitch } from './pitch';
import { keyToken, parseKey } from './key';

/**
 * Minimal-pair operations. Each changes one thing about the music so the ear can
 * hear exactly what that thing does. All operations are pure and deterministic.
 */
export type AlterationOp =
  /** In bars from–to, every note spelled `pc` (e.g. "F#") becomes `into` (e.g. "F") in the nearest octave. */
  | { kind: 'respell'; from: number; to: number; pc: string; into: string }
  /** Replace one bar with the prelude figuration of five pitches. */
  | { kind: 'revoice'; bar: number; pitches: string[] }
  /** Replace a single spelled pitch in one bar ("Bb3" → "C4"). */
  | { kind: 'swap'; bar: number; from: string; to: string }
  /** Remove bars from–to; later music moves earlier. Bar numbers keep their original values. */
  | { kind: 'cut'; from: number; to: number }
  /** Replace every note starting in [start, end) with new notes lasting to `end` (a re-harmonized chord). */
  | { kind: 'replaceSpan'; bar: number; start: number; end: number; notes: Array<{ name: string; line: number }> }
  /** Move bars from–to by a spelled interval (diatonic steps and semitones); their harmony labels move key with them. */
  | { kind: 'transpose'; from: number; to: number; steps: number; semitones: number };

function nearestOctave(originalMidi: number, name: string): { midi: number; name: string } {
  const pc = parsePitchClass(name);
  let best = { midi: 0, name: '', dist: Infinity };
  for (let octave = 0; octave <= 8; octave++) {
    const p = { ...pc, octave };
    const midi = midiOf(p);
    const d = Math.abs(midi - originalMidi);
    if (d < best.dist) best = { midi, name: pitchName(p), dist: d };
  }
  return { midi: best.midi, name: best.name };
}

function applyOne(core: PieceCore, op: AlterationOp): PieceCore {
  switch (op.kind) {
    case 'respell': {
      const target = parsePitchClass(op.pc);
      const notes = core.notes.map((n) => {
        if (n.bar < op.from || n.bar > op.to) return n;
        const p = parsePitch(n.name);
        if (!samePc(p, target)) return n;
        const moved = nearestOctave(n.midi, op.into);
        return { ...n, midi: moved.midi, name: moved.name, altered: true };
      });
      return { ...core, notes };
    }
    case 'swap': {
      const from = parsePitch(op.from);
      const to = parsePitch(op.to);
      const notes = core.notes.map((n) =>
        n.bar === op.bar && n.midi === midiOf(from) ? { ...n, midi: midiOf(to), name: pitchName(to), altered: true } : n,
      );
      return { ...core, notes };
    }
    case 'revoice': {
      const m = core.measures.find((x) => x.bar === op.bar);
      if (!m) return core;
      const kept = core.notes.filter((n) => n.bar !== op.bar);
      const added = preludeFiguration(op.pitches, m.start, op.bar, true);
      return { ...core, notes: [...kept, ...added].sort((a, b) => a.onset - b.onset || a.midi - b.midi) };
    }
    case 'replaceSpan': {
      const kept = core.notes.filter((n) => n.onset < op.start - 1e-6 || n.onset >= op.end - 1e-6);
      const added: NoteEvent[] = op.notes.map((x) => {
        const p = parsePitch(x.name);
        return { bar: op.bar, onset: op.start, dur: op.end - op.start, midi: midiOf(p), name: pitchName(p), line: x.line, altered: true };
      });
      return { ...core, notes: [...kept, ...added].sort((a, b) => a.onset - b.onset || a.midi - b.midi) };
    }
    case 'transpose': {
      const inRange = (bar: number) => bar >= op.from && bar <= op.to;
      const notes = core.notes.map((n) => {
        if (!inRange(n.bar)) return n;
        const p = transposePitch(parsePitch(n.name), op.steps, op.semitones);
        return { ...n, midi: midiOf(p), name: pitchName(p), altered: true };
      });
      const moveKey = (token: string) => {
        const k = parseKey(token);
        const t = transposePitch({ ...k.tonic, octave: 4 }, op.steps, op.semitones);
        return keyToken({ tonic: { letter: t.letter, alter: t.alter }, mode: k.mode });
      };
      const harmony = core.harmony.map((h) => (inRange(h.bar) ? { ...h, key: moveKey(h.key), ...(h.pivot ? { pivot: { ...h.pivot, key: moveKey(h.pivot.key) } } : {}) } : h));
      return { ...core, notes, harmony };
    }
    case 'cut': {
      const removed = core.measures.filter((m) => m.bar >= op.from && m.bar <= op.to);
      if (!removed.length) return core;
      const cutStart = removed[0].start;
      const cutLen = removed.reduce((a, m) => a + m.length, 0);
      const shift = (t: number) => (t >= cutStart + cutLen - 1e-9 ? t - cutLen : t);
      const measures: Measure[] = core.measures
        .filter((m) => m.bar < op.from || m.bar > op.to)
        .map((m) => ({ ...m, start: shift(m.start) }));
      const notes: NoteEvent[] = core.notes
        .filter((n) => n.bar < op.from || n.bar > op.to)
        .map((n) => ({ ...n, onset: shift(n.onset) }));
      const harmony: HarmonySpec[] = core.harmony.filter((h) => h.bar < op.from || h.bar > op.to);
      const pedals: PedalSpec[] = core.pedals
        .map((p) => ({ ...p, from: p.from >= op.from && p.from <= op.to ? op.to + 1 : p.from, to: p.to >= op.from && p.to <= op.to ? op.from - 1 : p.to }))
        .filter((p) => p.from <= p.to);
      return { ...core, measures, notes, harmony, pedals };
    }
  }
}

/** Apply operations in order, then replace any harmony labels given in `harmony`. */
export function applyAlterations(core: PieceCore, ops: AlterationOp[], harmony: HarmonySpec[] = []): PieceCore {
  let out = core;
  for (const op of ops) out = applyOne(out, op);
  if (harmony.length) {
    const replaced = new Set(harmony.map((h) => `${h.bar}:${h.beat ?? 0}`));
    out = { ...out, harmony: [...out.harmony.filter((h) => !replaced.has(`${h.bar}:${h.beat ?? 0}`)), ...harmony] };
  }
  return out;
}

/** The bars an alteration touches, for highlighting. */
export function alteredBars(ops: AlterationOp[]): number[] {
  const bars = new Set<number>();
  for (const op of ops) {
    if (op.kind === 'respell' || op.kind === 'cut' || op.kind === 'transpose') for (let b = op.from; b <= op.to; b++) bars.add(b);
    else bars.add(op.bar);

  }
  return [...bars].sort((a, b) => a - b);
}
